<script setup lang="ts">
import { nextTick } from 'vue';
import { dragRootElement } from '@/assets/js/utils';

interface Node {
  nodeKey: string;
  nodeContent: string;
  underline?: boolean;
  counterpoints?: Array<{
    team: {
      _id: string;
    };
  }>;
}

interface Excerpt {
  paragraphs: Array<{
    paragraphId: string;
    nodes: Array<Node>;
  }>;
}

interface Props {
  excerpt?: Excerpt;
  pinned: boolean;
  annotationId: string;
  showEllipses?: boolean;
  updateKey?: number;
  nodeBackgroundColor?: (node: Node) => string | undefined;
}

interface Emits {
  (e: 'pin', annotationId: string): void;
  (e: 'close', annotationId: string): void;
  (e: 'mouseleave'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const handlePin = async () => {
  emit('pin', props.annotationId);

  // Add drag functionality after pinning
  await nextTick();
  setTimeout(() => {
    dragRootElement(
      document.getElementById(`rootTextExcerptContainer-${props.annotationId}`),
      `paragraph-header-${props.annotationId}`
    );
  }, 100);
};

const handleClose = () => {
  emit('close', props.annotationId);
};
</script>

<template>
  <div
    :id="`rootTextExcerptContainer-${annotationId}`"
    :ref="`rootTextExcerptContainer-${annotationId}`"
    class="root-text-excerpt-container uk-border-rounded"
  >
    <div
      v-if="excerpt"
      @mouseleave="$emit('mouseleave')"
      :ref="`rootTextExcerpt-${annotationId}`"
      :key="updateKey"
    >
      <div
        class="uk-flex uk-flex-between paragraph-header"
        :id="`paragraph-header-${annotationId}`"
      >
        <span class="uk-text-small uk-text-bold" style="color: white">Near Context</span>
        <img
          v-if="!pinned"
          src="/icons/white/icon-white-pin-angle-fill.svg"
          @click="handlePin"
          alt="pin"
        />
        <span v-else uk-close @click="handleClose" style="padding-top: 5px"></span>
      </div>
      <div class="paragraph-container">
        <p v-for="(p, index) in excerpt.paragraphs" :key="p.paragraphId">
          <span v-if="showEllipses && index === 0">...</span>
          <span
            v-for="(node, nodeIndex) in p.nodes"
            :key="node.nodeKey"
            :style="{
              background: node.underline
                ? props.nodeBackgroundColor
                  ? props.nodeBackgroundColor(node)
                  : 'rgba(200, 221, 233, .5)'
                : undefined,
            }"
            :class="{
              underlineClass: node.underline,
            }"
            :id="`P_${nodeIndex}_N_${node.nodeKey}`"
            >{{ node.nodeContent }}</span
          >
          <span v-if="showEllipses && index === excerpt.paragraphs.length - 1">...</span>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.paragraph-container {
  padding: 12px;
}
</style>
